import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  Users, Video, CheckCircle, Clock, Award,
  Plus, ChevronRight
} from 'lucide-react';
import { APIConstant } from 'services/api-constant';
import styles from './reports/dashboard.module.scss';

// ─── Mock data ───────────────────────────────────────────────────
const TREND_DATA = [
  { day: 'Mon', interviews: 8,  passed: 5 },
  { day: 'Tue', interviews: 14, passed: 10 },
  { day: 'Wed', interviews: 11, passed: 7 },
  { day: 'Thu', interviews: 18, passed: 13 },
  { day: 'Fri', interviews: 22, passed: 16 },
  { day: 'Sat', interviews: 7,  passed: 4 },
  { day: 'Sun', interviews: 5,  passed: 3 },
];

const TECH_DATA = [
  { tech: 'React',   count: 32 },
  { tech: 'Java',    count: 28 },
  { tech: 'Python',  count: 24 },
  { tech: 'Node.js', count: 19 },
  { tech: 'AWS',     count: 14 },
  { tech: 'JS',      count: 22 },
];

const DONUT_DATA = [
  { name: 'Passed',  value: 62, color: '#00E5A3' },
  { name: 'On Hold', value: 20, color: '#7C5CFF' },
  { name: 'Failed',  value: 18, color: '#FF4D6D' },
];

const MOCK_INTERVIEWS = [
  { id: 1, candidate: 'Arjun Mehta',    technology: 'React',   interviewer: 'Priya K.',  score: 88, status: 'Passed',   date: 'Today, 10:30' },
  { id: 2, candidate: 'Sneha Rajan',    technology: 'Python',  interviewer: 'Kiran S.',  score: 72, status: 'On Hold',  date: 'Today, 09:00' },
  { id: 3, candidate: 'Rohan Das',      technology: 'Java',    interviewer: 'Anand M.',  score: 91, status: 'Passed',   date: 'Yesterday'    },
  { id: 4, candidate: 'Divya Nair',     technology: 'AWS',     interviewer: 'Priya K.',  score: 55, status: 'Failed',   date: 'Yesterday'    },
  { id: 5, candidate: 'Karthik Suresh', technology: 'Node.js', interviewer: 'Kiran S.',  score: 79, status: 'Passed',   date: '2 days ago'   },
  { id: 6, candidate: 'Aisha Banu',     technology: 'React',   interviewer: 'Anand M.',  score: 0,  status: 'Pending',  date: 'Today, 14:00' },
];

const BAR_COLORS = ['#00C8FF','#7C5CFF','#00E5A3','#FFB800','#FF4D6D','#00C8FF'];

// ─── Animated counter ────────────────────────────────────────────
function useCounter(target, duration = 1100) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!target) return;
    let current = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      current += step;
      if (current >= target) { setVal(target); clearInterval(timer); }
      else setVal(Math.floor(current));
    }, 16);
    return () => clearInterval(timer);
  }, [target]);
  return val;
}

// ─── Stat Card ───────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, suffix, color, trend, trendDir, delay }) {
  const count = useCounter(value);
  return (
    <div className={`${styles.statCard} ${styles[color]}`} style={{ animationDelay: delay }}>
      <div className={`${styles.statIcon} ${styles[color]}`}>
        <Icon size={18} />
      </div>
      {trend && <span className={`${styles.statTrend} ${styles[trendDir]}`}>{trend}</span>}
      <div className={styles.statValue}>{count.toLocaleString()}{suffix || ''}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}

// ─── Custom tooltip ──────────────────────────────────────────────
const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#0D1B2E', border: '1px solid rgba(0,200,255,0.2)', borderRadius: 8, padding: '10px 14px', fontFamily: 'Outfit,sans-serif', fontSize: 13 }}>
      <p style={{ color: '#7A93B4', margin: '0 0 6px', fontSize: 11 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, margin: '3px 0', fontWeight: 600 }}>
          {p.name}: <span style={{ color: '#E8F0FE' }}>{p.value}</span>
        </p>
      ))}
    </div>
  );
};

// ─── Badge ───────────────────────────────────────────────────────
function Badge({ status }) {
  const cls = { Passed: 'passed', Failed: 'failed', 'On Hold': 'onhold', Pending: 'pending', 'In Progress': 'inprogress' };
  return <span className={`${styles.badge} ${styles[cls[status] || 'pending']}`}>{status}</span>;
}

// ─── Dashboard ───────────────────────────────────────────────────
const Reports = () => {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState(MOCK_INTERVIEWS);
  const [greeting, setGreeting] = useState('');
  const [dateStr, setDateStr] = useState('');

  useEffect(() => {
    const h = new Date().getHours();
    setGreeting(h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening');
    setDateStr(new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }));
  }, []);

  useEffect(() => {
    axios.get(APIConstant.getInterviews).then(r => { if (r.data?.length) setInterviews(r.data.slice(0, 8)); }).catch(() => {});
  }, []);

  const passed   = interviews.filter(i => i.status === 'Passed').length  || 51;
  const onhold   = interviews.filter(i => i.status === 'On Hold').length || 16;
  const scored   = interviews.filter(i => i.score > 0);
  const avgScore = scored.length ? Math.round(scored.reduce((a, b) => a + b.score, 0) / scored.length) : 79;

  return (
    <div className={styles.dashboardRoot}>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <p className={styles.greeting}>{greeting}</p>
          <h1>Interview <span>Dashboard</span></h1>
          <p className={styles.subtitle}>{dateStr}</p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.btnSecondary} onClick={() => navigate('/admin/questions')}>
            <Plus size={14} /> Add Question
          </button>
          <button className={styles.btnPrimary} onClick={() => navigate('/admin/interviews')}>
            <Video size={14} /> Schedule Interview
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className={styles.statsGrid}>
        <StatCard icon={Users}       label="Total Candidates" value={20000}      color="cyan"   trend="↑ 12%" trendDir="up"   delay="0.05s" />
        <StatCard icon={Video}       label="Total Interviews" value={1600}        color="purple" trend="↑ 8%"  trendDir="up"   delay="0.10s" />
        <StatCard icon={CheckCircle} label="Passed"           value={passed}      color="green"  trend="↑ 5%"  trendDir="up"   delay="0.15s" />
        <StatCard icon={Clock}       label="On Hold"          value={onhold}      color="amber"  trend="→ 0%"  trendDir="flat" delay="0.20s" />
        <StatCard icon={Award}       label="Avg Score"        value={avgScore} suffix="%" color="cyan" trend="↑ 3%" trendDir="up" delay="0.25s" />
      </div>

      {/* Charts Row */}
      <div className={styles.chartsRow}>

        {/* Interview Activity */}
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Interview Activity — Last 7 Days</h3>
            <span className={styles.chartPeriod}>This Week</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={TREND_DATA} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="ga" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#00C8FF" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00C8FF" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gb" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#00E5A3" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#00E5A3" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="day" tick={{ fill: '#7A93B4', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#7A93B4', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTip />} />
              <Area type="monotone" dataKey="interviews" name="Scheduled" stroke="#00C8FF" strokeWidth={2} fill="url(#ga)" dot={false} />
              <Area type="monotone" dataKey="passed"     name="Passed"    stroke="#00E5A3" strokeWidth={2} fill="url(#gb)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* By Technology */}
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Interviews by Technology</h3>
            <span className={styles.chartPeriod}>All Time</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={TECH_DATA} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="tech" tick={{ fill: '#7A93B4', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#7A93B4', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTip />} />
              <Bar dataKey="count" name="Interviews" radius={[4, 4, 0, 0]}>
                {TECH_DATA.map((_, i) => <Cell key={i} fill={BAR_COLORS[i]} fillOpacity={0.85} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Row */}
      <div className={styles.bottomRow}>

        {/* Donut Distribution */}
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Result Distribution</h3>
            <span className={styles.chartPeriod}>This Month</span>
          </div>
          <div className={styles.donutWrap}>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={DONUT_DATA} cx="50%" cy="50%" innerRadius={52} outerRadius={78} dataKey="value" paddingAngle={3}>
                  {DONUT_DATA.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={(v) => [`${v}%`]} contentStyle={{ background: '#0D1B2E', border: '1px solid rgba(0,200,255,0.2)', borderRadius: 8, fontFamily: 'Outfit' }} />
              </PieChart>
            </ResponsiveContainer>
            <div className={styles.donutLegend}>
              {DONUT_DATA.map((d) => (
                <div key={d.name} className={styles.legendItem}>
                  <div className={styles.legendLeft}>
                    <span className={styles.dot} style={{ background: d.color }} />
                    {d.name}
                  </div>
                  <div className={styles.scoreBar}>
                    <div className={styles.bar}>
                      <div className={styles.fill} style={{ width: `${d.value}%`, background: d.color }} />
                    </div>
                    <span className={styles.val} style={{ color: d.color }}>{d.value}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Interviews Table */}
        <div className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <h3>Recent Interviews</h3>
            <button className={styles.viewAll} onClick={() => navigate('/admin/interviews')}>
              View All <ChevronRight size={12} style={{ verticalAlign: 'middle' }} />
            </button>
          </div>
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Technology</th>
                  <th>Interviewer</th>
                  <th>Score</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {interviews.slice(0, 8).map((iv, i) => (
                  <tr key={iv.id || i}>
                    <td>{iv.candidate || iv.candidate_name || '—'}</td>
                    <td className={styles.dimText}>{iv.technology || '—'}</td>
                    <td className={styles.dimText}>{iv.interviewer || iv.interviewer_name || '—'}</td>
                    <td>
                      {iv.score > 0 ? (
                        <div className={styles.scoreBar}>
                          <div className={styles.bar}><div className={styles.fill} style={{ width: `${iv.score}%` }} /></div>
                          <span className={styles.val}>{iv.score}</span>
                        </div>
                      ) : <span className={styles.dimText}>—</span>}
                    </td>
                    <td><Badge status={iv.status || 'Pending'} /></td>
                    <td className={styles.dimText}>{iv.date || iv.date_time || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Reports;